import * as React from "react";
import {useEffect, useState} from "react";

import {BaseSlider} from 'components/forms/formComponents/_baseSlider';

import './style.scss';
import {useQuery} from "@tanstack/react-query";
import {Margin, HeadlineLevel} from "features/ui/enums";
import {ApartmentViewerServiceQueryKeys} from "features/apartmentViewer/services/ApartmentViewerService/config";
import {ApartmentViewerQueryMethods} from "features/apartmentViewer/services/ApartmentViewerService/query";
import {useInterval} from "components/hooks/useInterval";
import {ImageSizes} from "@organisms/Shell/image-size-enum";
import {AsyncImage} from "@atoms/Media/AsyncImage";
import {getImageUrlFromCDN} from "@atoms/Media/AsyncImage/helper";


import {Icon} from "@atoms/Media/Icon";
import {
    ApartmentFurnishSelector
} from "features/apartmentViewer/components/ApartmentFurnishSelector/ApartmentFurnishSelector";
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";
import {FurnishingModel} from "features/apartmentViewer/ApartmentViewer/models/FurnishingModel";


import {FormattedMessage} from "react-intl";
import { getSunstudyDate } from "features/services/utils/date-helpers";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    stageId: string;
    objectName: string;
}

export const ApartmentViewerSunStudy : React.FunctionComponent<Props> = ({stageId, objectName}) => {

    const [currentFurnishing, setCurrentFurnishing] = React.useState<string>("");
    const [currentFrame, setCurrentFrame] = React.useState<number>(0);
    const [frames, setFrames] = useState<string[]>([]);
    const [loading, _setLoading] = React.useState<boolean>(false);


    const {data} = useQuery({
        queryKey: ApartmentViewerServiceQueryKeys.getApartmentData(stageId, objectName),
        queryFn: () => ApartmentViewerQueryMethods.getApartmentData(stageId, objectName),
    });


    useEffect(() => {
        if(data && data.views && Object.keys(data.views).length > 0) {

            let defaultView = data.config.defaultArchetypeId;
            const defaultExists = data.views[defaultView];

            if(!defaultExists) {
                defaultView = Object.keys(data.views)[0];
            }


            setCurrentFurnishing(defaultView);

            const sunStudy = data.views[defaultView].minimap.storeys[0];

            if(sunStudy) {
                const defFrame = sunStudy.sunsimFiles.indexOf(sunStudy.defaultSunsimFile);
                setCurrentFrame(defFrame);
                setFrames(sunStudy.sunsimFiles);
            }

        }
    }, [data]);

    const interval = useInterval(() => {
        setCurrentFrame((frame) => {
            const newFrame = frame + 1;

            if(newFrame === frames.length) {
                return 0;
            }

            return newFrame;
        })
    }, 500);

    useEffect(() => {

        return () => {
            interval.stop();
        }
    }, []);

    const getImageUrl = (frame: number) => {
        if(!data || !currentFurnishing) return {
            url: '',
        };

        const url = `/externalfiles/image/residenceexplorer/${stageId}/360/${data.views[currentFurnishing].apartment.id}/${currentFurnishing}/minimap/${frames[frame]}`;


        return {
            url: url,
        }
    }

    const start = () => {
        interval.start();
    }

    const pause = () => {
        interval.stop();
    }

    const getSliderLabel = (value: number) =>  {

        const hour = value + 1;
        const isMidnight = hour === 24;


        return `Kl. ${isMidnight ? '00' : hour}:00`;
    }

    const onFurnishingClick = (furnishing: FurnishingModel) => {
        setCurrentFurnishing(furnishing.id);
    }

    const getDate = () => {
        if(!data || !currentFurnishing) return '';
        const sunStudy = data.views[currentFurnishing].minimap.storeys[0];

        if(!sunStudy) return '';

        const date = sunStudy.sunsimFilesTimeStamps[0];

        if(!date) return '';

        return getSunstudyDate(new Date(date));
    }

    if(!data || !data.views || Object.keys(data.views).length === 0) return null;

    return (
        <div className="apartment-viewer-sun-study">

            <div className="apartment-viewer-sun-study__header">
                <Headline level={HeadlineLevel.H2} margin={Margin.L}>
                    <FormattedMessage id="jm.components.ObjectPage.sunStudyTab.modalTitle" />
                </Headline>


                <ApartmentFurnishSelector siteData={new SiteModel(data)} onClick={onFurnishingClick} currentFurnishing={currentFurnishing} loading={loading} />
            </div>


            <div className="apartment-viewer-sun-study__content">
                <AsyncImage
                    image={getImageUrl(currentFrame)}
                    quality={ImageSizes.SIZE_1200}
                    className="residence-explorer-sun-study-view__image residence-explorer-sun-study-view__image--current"
                    alt="Sun study"
                    fullWidth
                    noCrop
                />

                <div className="residence-explorer-sun-study-view__load-bay">
                    {frames.map((image, index) => (
                        <img
                            src={getImageUrlFromCDN(getImageUrl(index), ImageSizes.SIZE_900, true)}
                            alt="Sun study"
                            key={`${index}`}
                            className="residence-explorer-sun-study-view__image"
                        />
                    ))}
                </div>
            </div>

            <div className="apartment-viewer-sun-study__controls">

                <div className="apartment-viewer-sun-study__date">
                    {getDate()}
                </div>

                <div className="apartment-viewer-sun-study__slider">
                    <div className="sun-study-slider__icons">
                        <div className="sun-study-slider__icon sun-study-slider__icon--up">
                            <Icon name="arrow-link" size={12} left />
                            <Icon name="half-sun" size={32} />
                        </div>

                        <div>
                            {getSliderLabel(currentFrame)}
                        </div>

                        <div className="sun-study-slider__icon sun-study-slider__icon--down">
                            <Icon name="half-sun" size={32} />
                            <Icon name="arrow-link" size={12} right />
                        </div>
                    </div>

                    <BaseSlider
                        restrictToMarks
                        value={currentFrame}
                        onChange={setCurrentFrame}
                        min={0}
                        label={null}
                        max={frames.length - 1}
                        marks={Array.from(frames).map((_, index) => ({ value: index }))}
                    />
                </div>

                <div
                    className="sun-study-controlls__play"
                    onClick={() => !interval.active ? start() : pause()}
                >
                    <Icon name="sun-study-control"/>
                    {!interval.active &&
                        <Icon name="play"/>
                    }
                    {interval.active &&
                        <Icon name="pause"/>
                    }
                </div>


            </div>
        </div>
    )
}
